/**
 * SignupWizardShell — shared chrome for the 3-step signup popup
 * sequence: Security Check → Email Verification → Application Password.
 *
 * All three steps render inside this shell so they look like 3 steps
 * of a single wizard rather than 3 unrelated modals. The shell:
 *   • Forces light-mode (the popup chrome is the same regardless of
 *     the system theme).
 *   • Pins a sage-gradient header with the step icon, title, copy
 *     and a 3-dot step indicator (1/2/3 of 3).
 *   • Renders a single body slot for the step-specific content.
 *   • Provides a consistent X close button.
 *
 * Children render INSIDE the wizard card and should not provide their
 * own outer container, backdrop, or title — that's the shell's job.
 */
import React from 'react';
import { X } from 'lucide-react';

const STEP_LABELS = {
  1: { title: 'Quick Security Check', subtitle: 'Verify you\'re human to continue.' },
  2: { title: 'Verify Your Email', subtitle: 'Confirm the email you used to sign up.' },
  3: { title: 'Application Password', subtitle: 'Enter the password you were given after submitting your application.' },
};

export default function SignupWizardShell({
  open,
  step,           // 1 | 2 | 3
  totalSteps = 3,
  icon,           // <Lock /> / <Mail /> / <Shield />
  onClose,
  children,
  testId,
}) {
  if (!open) return null;
  const meta = STEP_LABELS[step] || STEP_LABELS[1];

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 signup-wizard-light"
      role="dialog"
      aria-modal="true"
      data-testid={testId}
    >
      {/* Force light-mode regardless of system theme. The `signup-wizard-light`
          class scopes a CSS reset that disables all `dark:` variants for the
          card content. */}
      <style>{`
        .signup-wizard-light, .signup-wizard-light * {
          color-scheme: light !important;
        }
      `}</style>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden text-slate-900 relative">
        {/* Sage-gradient header band */}
        <div className="bg-gradient-to-br from-[#586D52] to-[#3d4f3a] p-6 text-white relative">
          <button
            type="button"
            onClick={() => onClose?.('user_cancelled')}
            className="absolute top-3 right-3 text-white/80 hover:text-white"
            aria-label="Cancel"
            data-testid="wizard-close-btn"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3">
            {icon}
          </div>

          <div className="flex items-center justify-between gap-2 mb-1">
            <h2 className="text-xl font-bold">{meta.title}</h2>
            <span className="text-[11px] font-medium text-white/85 tabular-nums whitespace-nowrap">
              Step {step} of {totalSteps}
            </span>
          </div>

          <p className="text-sm text-white/85">{meta.subtitle}</p>

          {/* Pip indicator */}
          <div className="flex gap-1.5 mt-3" aria-hidden="true">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((n) => (
              <span
                key={n}
                className={`h-1 rounded-full transition-all ${
                  n === step ? 'w-8 bg-white' : n < step ? 'w-4 bg-white/70' : 'w-4 bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Body slot — children render their step-specific content here */}
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
